import React, { useState, ReactNode } from "react";

interface TooltipProps {
  tooltip: string;
  children: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  disabled?: boolean;
  // When true, the bubble wraps to multiple lines with a capped width instead
  // of staying on a single no-wrap line. Use for full-sentence explanations.
  multiline?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  tooltip,
  children,
  position = "top",
  disabled,
  multiline,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const positionClasses = {
    top: "bottom-full mb-2 left-1/2 -translate-x-1/2",
    bottom: "top-full mt-2 left-1/2 -translate-x-1/2",
    left: "right-full mr-2 top-1/2 -translate-y-1/2",
    right: "left-full ml-2 top-1/2 -translate-y-1/2",
  };

  const arrowPosition = {
    top: "top-full left-1/2 -translate-x-1/2",
    bottom: "bottom-full left-1/2 -translate-x-1/2",
    left: "left-full top-1/2 -translate-y-1/2",
    right: "right-full top-1/2 -translate-y-1/2",
  };

  return disabled ? (
    children
  ) : (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      // Keyboard parity with hover: a tooltip only reachable by mouse is
      // invisible to anyone tabbing through checkout.
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      onKeyDown={(e) => {
        if (e.key === "Escape") setShowTooltip(false);
      }}
    >
      {children}

      {showTooltip && (
        <div
          className={`absolute z-10 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg shadow-md transform ${
            multiline
              ? "w-56 max-w-[80vw] text-center leading-snug normal-case"
              : "whitespace-nowrap"
          } ${positionClasses[position]}`}
        >
          {tooltip}
          <div
            className={`absolute w-2 h-2 bg-gray-800 rotate-45 ${arrowPosition[position]}`}
          ></div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;
